import { settingApi } from 'src/pages/setting/slice/settingApi';

type ApiResponse<T> = T | { data?: T };

const unwrapResponse = <T>(response: ApiResponse<T>, fallback: T): T => {
  if (response && typeof response === 'object' && 'data' in response) {
    return (response as { data?: T }).data ?? fallback;
  }
  return (response as T) ?? fallback;
};

function parseCapabilityFields(raw: Record<string, unknown>): {
  capabilityKey: string;
  channel: string;
  provider: string | null;
} {
  const capabilityKey = String(raw.capabilityKey ?? raw.channel ?? '').trim();
  const channelFamily = String(raw.channel ?? '').trim();
  const fromKey = capabilityKey.includes(':') ? capabilityKey.split(':')[0]! : capabilityKey;
  const channel = (channelFamily || fromKey || capabilityKey).trim();
  let provider: string | null =
    raw.provider != null && String(raw.provider) !== '' ? String(raw.provider) : null;
  if (!provider && capabilityKey.includes(':')) {
    provider = capabilityKey.slice(capabilityKey.indexOf(':') + 1) || null;
  }
  const resolvedKey = capabilityKey || channel;
  return { capabilityKey: resolvedKey, channel, provider };
}

export function normalizeChannelFeatureFlag(raw: unknown): ChannelFeatureFlag {
  if (!raw || typeof raw !== 'object') {
    return {
      capabilityKey: '',
      channel: '',
      provider: null,
      enabled: false,
      source: 'plan',
      overrideValue: null,
      planEnabled: false,
      platformBlocked: false,
    };
  }
  const r = raw as Record<string, unknown>;
  const { capabilityKey, channel, provider } = parseCapabilityFields(r);
  return {
    capabilityKey,
    channel,
    provider,
    enabled: Boolean(r.enabled),
    source: r.source === 'tenant_override' ? 'tenant_override' : 'plan',
    overrideValue:
      r.overrideValue === null || r.overrideValue === undefined
        ? null
        : Boolean(r.overrideValue),
    planEnabled: Boolean(r.planEnabled),
    platformBlocked: Boolean(r.platformBlocked),
  };
}

export type SupportedChannelFlag =
  | 'WHATSAPP'
  | 'EMAIL'
  | 'LIVE_CHAT'
  | 'MESSENGER'
  | 'VOICE'
  | 'INSTAGRAM'
  | 'TELEGRAM'
  | 'SMS'
  | 'LINE'
  | 'MICROSOFTTEAMS';

export type ChannelFeatureFlag = {
  /** API row identity, e.g. WHATSAPP or EMAIL:GOOGLE (see CHANNEL_FLAGGING_FRONTEND_API.md). */
  capabilityKey: string;
  /** Channel family (EMAIL, SMS, …). */
  channel: string;
  provider: string | null;
  enabled: boolean;
  source: 'plan' | 'tenant_override';
  overrideValue: boolean | null;
  planEnabled: boolean;
  platformBlocked: boolean;
};

export type ChannelFeatureFlagAudit = {
  id: string;
  tenantId: string;
  userId: string;
  entityType: 'channel_feature_flag';
  action: string;
  roleId: string;
  previousValue?: ChannelFeatureFlag | null;
  newValue?: ChannelFeatureFlag | null;
  changedAt: string;
};

/** POST `api/admin/feature-flags/channels/audit` — see CHANNEL_FLAGGING_FRONTEND_API.md §A4. */
export type ChannelAuditPayload = {
  filters: unknown[];
  globalSearch: string;
  page: number;
  limit: number;
  action?: string;
  channel?: string;
  startDate?: string;
  endDate?: string;
};

export type ChannelFeatureFlagAuditResponse = {
  list: ChannelFeatureFlagAudit[];
  total: number;
  page?: number;
  limit?: number;
  totalPages?: number;
};

export type ChannelPlatformPolicyData = {
  tenantId: string;
  /** Platform blocks only; keys may be families (EMAIL) or composite (EMAIL:GOOGLE). */
  allowances: Partial<Record<string, boolean>>;
};

export type RoleChannelAccess = {
  id: string;
  roleId: string;
  /** Family (WHATSAPP) or composite (EMAIL:GOOGLE) per API. */
  channel: string;
  isEnabled: boolean;
  updatedBy: string;
  updatedAt: string;
};

export type UserChannelAccess = {
  id: string;
  userId: string;
  channel: string;
  isEnabled: boolean;
  updatedBy: string;
  updatedAt: string;
};

export const channelFeatureFlagApi = settingApi.injectEndpoints({
  endpoints: (builder) => ({
    getChannelFeatureFlags: builder.query<ChannelFeatureFlag[], void>({
      query: () => ({
        url: 'api/admin/feature-flags/channels',
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<ChannelFeatureFlag[]>) => {
        const raw = unwrapResponse(response, [] as unknown[]);
        if (!Array.isArray(raw)) return [];
        return raw
          .filter((row) => row && typeof row === 'object')
          .map((row) => normalizeChannelFeatureFlag(row));
      },
    }),

    setChannelFeatureFlag: builder.mutation<
      ChannelFeatureFlag,
      { capabilityKey: string; enabled: boolean }
    >({
      query: ({ capabilityKey, enabled }) => ({
        url: `api/admin/feature-flags/channels/${encodeURIComponent(capabilityKey)}`,
        method: 'POST',
        body: { enabled },
      }),
      transformResponse: (response: ApiResponse<ChannelFeatureFlag>) =>
        normalizeChannelFeatureFlag(unwrapResponse(response, null)),
    }),

    /** POST .../channels/set (A2b) — same semantics as `setChannelFeatureFlag` without path encoding. */
    setChannelFeatureFlagViaBody: builder.mutation<
      ChannelFeatureFlag,
      { channel: string; enabled: boolean }
    >({
      query: ({ channel, enabled }) => ({
        url: 'api/admin/feature-flags/channels/set',
        method: 'POST',
        body: { channel, enabled },
      }),
      transformResponse: (response: ApiResponse<ChannelFeatureFlag>) =>
        normalizeChannelFeatureFlag(unwrapResponse(response, null)),
    }),

    resetChannelFeatureFlag: builder.mutation<ChannelFeatureFlag, { capabilityKey: string }>({
      query: ({ capabilityKey }) => ({
        url: `api/admin/feature-flags/channels/${encodeURIComponent(capabilityKey)}`,
        method: 'DELETE',
      }),
      transformResponse: (response: ApiResponse<ChannelFeatureFlag>) =>
        normalizeChannelFeatureFlag(unwrapResponse(response, null)),
    }),

    getChannelFeatureFlagAudit: builder.mutation<
      ChannelFeatureFlagAuditResponse,
      ChannelAuditPayload
    >({
      query: (payload) => ({
        url: 'api/admin/feature-flags/channels/audit',
        method: 'POST',
        body: payload,
      }),
      transformResponse: (response: ApiResponse<ChannelFeatureFlagAuditResponse>) => {
        const u = unwrapResponse(response, { list: [], total: 0 });
        return {
          list: Array.isArray(u.list) ? u.list : [],
          total: typeof u.total === 'number' ? u.total : 0,
          page: u.page,
          limit: u.limit,
          totalPages: u.totalPages,
        };
      },
    }),

    getChannelPlatformPolicy: builder.query<ChannelPlatformPolicyData, { tenantId: string }>({
      query: ({ tenantId }) => ({
        url: `api/tenants/${tenantId}/channel-platform-policy`,
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<ChannelPlatformPolicyData>) =>
        unwrapResponse(response, { tenantId: '', allowances: {} }),
    }),

    setChannelPlatformPolicy: builder.mutation<
      ChannelPlatformPolicyData,
      { tenantId: string; channel: string; allowed: boolean }
    >({
      query: ({ tenantId, channel, allowed }) => ({
        url: `api/tenants/${tenantId}/channel-platform-policy`,
        method: 'PUT',
        body: { channel, allowed },
      }),
      transformResponse: (response: ApiResponse<ChannelPlatformPolicyData>) =>
        unwrapResponse(response, { tenantId: '', allowances: {} }),
    }),

    setRoleChannelAccess: builder.mutation<
      RoleChannelAccess[],
      { roleId: string; channel: string; isEnabled: boolean }
    >({
      query: ({ roleId, channel, isEnabled }) => ({
        url: `api/admin/roles/channel-access/role/${roleId}/set`,
        method: 'POST',
        body: { channel, isEnabled },
      }),
      transformResponse: (response: ApiResponse<RoleChannelAccess[]>) => unwrapResponse(response, []),
    }),

    getRoleChannelAccess: builder.query<RoleChannelAccess[], { roleId: string }>({
      query: ({ roleId }) => ({
        url: `api/admin/roles/channel-access/role/${roleId}`,
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<RoleChannelAccess[]>) => unwrapResponse(response, []),
    }),

    setUserChannelAccess: builder.mutation<
      UserChannelAccess[],
      { userId: string; channel: string; isEnabled: boolean | null }
    >({
      query: ({ userId, channel, isEnabled }) => ({
        url: 'api/admin/roles/channel-access/user/set',
        method: 'POST',
        body: { userId, channel, isEnabled },
      }),
      transformResponse: (response: ApiResponse<UserChannelAccess[]>) => unwrapResponse(response, []),
    }),

    getUserChannelAccess: builder.query<UserChannelAccess[], { userId: string }>({
      query: ({ userId }) => ({
        url: `api/admin/roles/channel-access/user/${userId}`,
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<UserChannelAccess[]>) => unwrapResponse(response, []),
    }),
  }),
});

export const {
  useGetChannelFeatureFlagsQuery,
  useSetChannelFeatureFlagMutation,
  useSetChannelFeatureFlagViaBodyMutation,
  useResetChannelFeatureFlagMutation,
  useGetChannelFeatureFlagAuditMutation,
  useGetChannelPlatformPolicyQuery,
  useSetChannelPlatformPolicyMutation,
  useSetRoleChannelAccessMutation,
  useGetRoleChannelAccessQuery,
  useSetUserChannelAccessMutation,
  useGetUserChannelAccessQuery,
} = channelFeatureFlagApi;

